import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getShopifyCollections } from "@redotech/redo-merchant-app-common/client/shopify";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { CollectionState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/collection";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useLayoutEffect, useMemo, useState } from "react";
import { getBigcommerceCollections } from "../../../client/bigcommerce";
import { getCommentsoldCollections } from "../../../client/commentsold";

export const CollectionDetails = memo(function CollectionDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<CollectionState>) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [searchString, setSearchString] = useState("");
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  const [collectionOptionsLoad, doCollectionsOptionsLoad] = useTriggerLoad(
    async (signal) => {
      let shopifyCollections: { id: string; name: string }[] = [];
      let csCollections: { id: number; name: string }[] = [];
      let bigcommerceCollections: { id: number; name: string }[] = [];

      try {
        shopifyCollections = await getShopifyCollections(client, {
          searchString,
          signal,
        });
      } catch (e) {
        console.error("failed to get shopify Collections");
      }

      try {
        csCollections = await getCommentsoldCollections(client, {
          searchString,
          signal,
        });
      } catch (e) {
        console.error("failed to get commentsold collections");
      }

      try {
        bigcommerceCollections = await getBigcommerceCollections(client, {
          searchString,
          signal,
        });
      } catch (e) {
        console.error(
          "failed to get bigcommerce collections from return flow condition setting",
          e,
        );
      }

      setCollectionsLoading(false);
      const collections = [
        ...shopifyCollections,
        ...csCollections,
        ...bigcommerceCollections,
      ];

      let collectionNames = collections.map(
        (collection: { id: string | number; name: string }) => {
          return collection.name;
        },
      );

      collectionNames = Array.from(new Set(collectionNames));

      return { collections: collectionNames };
    },
  );
  useLayoutEffect(() => {
    doCollectionsOptionsLoad();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString]);

  const collectionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const collections = collectionOptionsLoad.value?.collections || [];
    const allCollectionNames = new Set([...collections, ...state]);
    return Array.from(allCollectionNames).map((collectionName) => ({
      id: collectionName,
      value: collectionName,
      type: "badge" as const,
      badge: { text: collectionName, color: "gray" as const },
    }));
  }, [collectionOptionsLoad.value?.collections, state]);

  const selectedOptions = useMemo(
    () => collectionOptions.filter((option) => state.includes(option.value)),
    [collectionOptions, state],
  );

  return (
    <RedoTagsDropdownInput
      description="The first 100 collections are shown by default. To access more, search by title."
      disabled={disabled}
      label="Collections"
      loading={collectionsLoading}
      options={collectionOptions}
      placeholder="Select collections..."
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={(value) => {
        setSearchString(value);
        setCollectionsLoading(true);
      }}
      setSelectedOptions={(items: BadgeRedoListItem<string>[]) => {
        setState(items.map((item) => item.value));
      }}
      size="xs"
    />
  );
});
